import React, { Suspense, lazy } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import AppNavbar from './components/Navbar';
import CookieBanner from './components/CookieBanner';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ProtectedRoute from './pages/ProtectedRoute';

// Signed-in pages and the manual load on demand, so the landing page ships a smaller bundle.
// Each gets its own Suspense: wrapping IonRouterOutlet in one breaks its swipe-back setup.
const page = (load: () => Promise<{ default: React.ComponentType<any> }>) => {
  const Lazy = lazy(load);
  const Page: React.FC<any> = (props) => (
    <Suspense fallback={null}>
      <Lazy {...props} />
    </Suspense>
  );
  return Page;
};
const Dashboard = page(() => import('./pages/Dashboard'));
const TestSuiteCreate = page(() => import('./pages/TestSuiteCreate'));
const TestSuiteDetail = page(() => import('./pages/TestSuiteDetail'));
const TestRunner = page(() => import('./pages/TestRunner'));
const TestResults = page(() => import('./pages/TestResults'));
const TestResultDetail = page(() => import('./pages/TestResultDetail'));
const ProjectCreate = page(() => import('./pages/ProjectCreate'));
const ProjectDetail = page(() => import('./pages/ProjectDetail'));
const ProjectEdit = page(() => import('./pages/ProjectEdit'));
const Settings = page(() => import('./pages/Settings'));
const Subscription = page(() => import('./pages/Subscription'));
const UserManual = page(() => import('./pages/UserManual'));

setupIonicReact();

const AuthenticatedApp: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  return (
    <>
      <AppNavbar />
      <IonRouterOutlet>
        {/* Public routes */}
        <Route exact path="/" component={Home} />
        <Route exact path="/login" component={Login} />
        <Route exact path="/register" component={Register} />
        <Route exact path="/forgot-password" component={ForgotPassword} />
        <Route exact path="/reset-password" component={ResetPassword} />
        <Route exact path="/manual" component={UserManual} />

        {/* Protected routes */}
        <ProtectedRoute exact path="/dashboard" component={Dashboard} />
        <ProtectedRoute exact path="/create" component={TestSuiteCreate} />
        <ProtectedRoute exact path="/suite/:id" component={TestSuiteDetail} />
        <ProtectedRoute exact path="/suite/:id/run" component={TestRunner} />
        <ProtectedRoute exact path="/suite/:id/run/:runId" component={TestRunner} />
        <ProtectedRoute exact path="/runs/:runId/results" component={TestResults} />
        <ProtectedRoute exact path="/results/:resultId" component={TestResultDetail} />
        <ProtectedRoute exact path="/projects/create" component={ProjectCreate} />
        <ProtectedRoute exact path="/project/:id" component={ProjectDetail} />
        <ProtectedRoute exact path="/project/:id/edit" component={ProjectEdit} />
        <ProtectedRoute exact path="/settings" component={Settings} />
        <ProtectedRoute exact path="/subscription" component={Subscription} />

        {/* Catch-all: send authenticated users to dashboard, others to home */}
        <Route render={() => <Redirect to={isAuthenticated ? '/dashboard' : '/'} />} />
      </IonRouterOutlet>
    </>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <IonApp>
      <AuthProvider>
        <NotificationProvider>
          <IonReactRouter>
            <AuthenticatedApp />
          </IonReactRouter>
        </NotificationProvider>
      </AuthProvider>
      <Toaster position="top-center" />
      <CookieBanner />
    </IonApp>
  </HelmetProvider>
);

export default App;
